import React from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, getBestBadgeVariant } from "@/lib/utils";

/** React Query for one report: cached per report + filters, refetched when the filters change. */
export function useReportQuery<T>(reportKey: string, params: unknown, load: () => Promise<T>, enabled = true) {
	return useQuery({
		queryKey: ["report", reportKey, params],
		queryFn: load,
		enabled,
		staleTime: 60 * 1000,
	});
}

/** Loading → skeleton, failure → retry, success → children(data). A failed load is never shown as "no data". */
export function ReportState<T>({
	query,
	children,
	tiles = 4,
}: {
	query: UseQueryResult<T>;
	children: (data: T) => React.ReactNode;
	/** Skeleton tiles to show while loading. */
	tiles?: number;
}) {
	if (query.isPending) return <ReportSkeleton tiles={tiles} />;
	if (query.isError) return <ReportError onRetry={() => query.refetch()} />;
	return <>{children(query.data as T)}</>;
}

export function ReportSkeleton({ tiles = 4 }: { tiles?: number }) {
	const { t } = useTranslation();
	return (
		<div className="space-y-4" aria-busy="true" aria-label={t("common.loading")}>
			{tiles > 0 && (
				<StatGrid>
					{Array.from({ length: tiles }, (_, i) => (
						<div key={i} className="rounded-2xl border bg-card p-4">
							<Skeleton className="h-4 w-24" />
							<Skeleton className="mt-3 h-7 w-32" />
						</div>
					))}
				</StatGrid>
			)}
			<div className="space-y-3 rounded-2xl border bg-card p-4">
				{Array.from({ length: 6 }, (_, i) => (
					<Skeleton key={i} className="h-8 w-full" />
				))}
			</div>
		</div>
	);
}

export function ReportError({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation();
	return (
		<div
			role="alert"
			className="flex flex-col items-center gap-3 rounded-2xl border bg-card px-4 py-12 text-center"
		>
			<AlertCircle className="h-8 w-8 text-destructive" aria-hidden="true" />
			<p className="font-semibold">{t("reports.errors.tabLoadFailed")}</p>
			<p className="max-w-md text-sm text-muted-foreground">{t("reports.errors.retryHint")}</p>
			<Button variant="outline" size="sm" onClick={onRetry}>
				<RefreshCw className="h-4 w-4 me-2" />
				{t("common.retry")}
			</Button>
		</div>
	);
}

/** Headline numbers of a report: one column on phones, two on tablets, up to four on wide screens. */
export function StatGrid({ children, className }: { children: React.ReactNode; className?: string }) {
	return <div className={cn("grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4", className)}>{children}</div>;
}

/** Two panels side by side on wide screens (chart + breakdown), stacked on phones. */
export function SplitRow({ children, className }: { children: React.ReactNode; className?: string }) {
	return <div className={cn("grid gap-4 lg:grid-cols-2", className)}>{children}</div>;
}

/** Compact status pill for table cells (the shared Badge is sized for page headers). */
export function StatusBadge({ status, label }: { status: string | null | undefined; label: string }) {
	if (!status) return <>-</>;
	return (
		<Badge variant={getBestBadgeVariant(status) ?? "outline"} className="px-2 py-0.5 font-medium">
			{label}
		</Badge>
	);
}

/** Small segmented control (e.g. Day / Week / Month). */
export function Segmented<T extends string>({
	value,
	options,
	onChange,
	label,
}: {
	value: T;
	options: { value: T; label: string }[];
	onChange: (value: T) => void;
	label: string;
}) {
	return (
		<div
			role="radiogroup"
			aria-label={label}
			className="inline-flex rounded-lg border bg-muted/40 p-0.5 print:hidden"
		>
			{options.map(o => (
				<button
					key={o.value}
					type="button"
					role="radio"
					aria-checked={value === o.value}
					onClick={() => onChange(o.value)}
					className={cn(
						"rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
						value === o.value
							? "bg-card text-foreground shadow-sm"
							: "text-muted-foreground hover:text-foreground",
					)}
				>
					{o.label}
				</button>
			))}
		</div>
	);
}

/** Titled panel for a chart or breakdown. */
export function Panel({
	title,
	actions,
	children,
	className,
}: {
	title: string;
	actions?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<section className={cn("min-w-0 rounded-2xl border bg-card p-4 shadow-sm", className)}>
			<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
				<h3 className="text-sm font-semibold">{title}</h3>
				{actions}
			</div>
			{children}
		</section>
	);
}

/** A short explanation of how a report counts (what is included, what is not), shown under it. */
export function ReportNote({ children }: { children: React.ReactNode }) {
	return <p className="px-1 text-xs leading-relaxed text-muted-foreground">{children}</p>;
}
